import classNames from 'classnames';
import React, {useRef} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Label from 'ui-library/bs/Label';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import PlaceholderText from 'ui-library/PlaceholderText';
import uniqueId from 'util/uniqueId';

/**
 * Component for selecting the [source, destination] column when inserting samples into a
 * structured table. It is a controlled component; the caller must keep track of the current
 * selection by using the `onChange` callback.
 *
 * When `selectedColumnIndex` is `null`, no Option is selected and `placeholder` (if provided) is
 * rendered as ghost-text inside the closed dropdown (placeholder is NOT a selectable option).
 * When `label` is omitted, the visible Label above the dropdown is not rendered.
 */

export interface ColumnSelectorProps {
  label?: string;
  placeholder?: string;
  columns: Array<{
    name: string;
    icon?: BenchlingIconName;
    /** When true, the menu Option is rendered disabled and not selectable. */
    isDisabled?: boolean;
    /**
     * Optional, lighter-gray suffix shown after the option name when present
     * (e.g. "(Already mapped)"). Useful with `isDisabled` to explain why an
     * option can't be picked.
     */
    suffix?: string;
  }>;
  onChange: (columnIndex: number) => void;
  selectedColumnIndex: number | null;
  disabled?: boolean;
  disableWhenOnlyOneColumn?: boolean;
}

export default function ColumnSelector(props: ColumnSelectorProps): JSX.Element {
  const controlId = useRef(uniqueId('colselector_'));
  const {columns, selectedColumnIndex, placeholder} = props;
  const hasSelection = selectedColumnIndex != null && columns[selectedColumnIndex] != null;
  const selectedName = hasSelection ? columns[selectedColumnIndex].name : '';
  const displayName = hasSelection ? (
    selectedName
  ) : placeholder != null ? (
    <PlaceholderText value={placeholder} />
  ) : (
    ''
  );
  const disableWhenOnlyOneColumn = props.disableWhenOnlyOneColumn ?? true;
  return (
    <span {...getTestProps({component: 'ColumnSelector', element: 'root'})}>
      {props.label == null ? null : (
        <Label
          htmlFor={controlId.current}
          className={classNames('u-small-spacing-top', props.disabled && 'faded')}
          {...getTestProps({component: 'ColumnSelector', element: 'label'})}
        >
          {props.label}
        </Label>
      )}
      <SelectDropdown
        id={controlId.current}
        disabled={Boolean(props.disabled) || (disableWhenOnlyOneColumn && columns.length === 1)}
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={selectedName}>
            {columns.map((columnInfo, index) => (
              <Option
                key={columnInfo.name + index}
                icon={columnInfo.icon}
                value={columnInfo.name}
                isDisabled={columnInfo.isDisabled}
                onSelect={
                  columnInfo.isDisabled
                    ? undefined
                    : () => {
                        props.onChange(index);
                      }
                }
              >
                {columnInfo.suffix == null ? (
                  columnInfo.name
                ) : (
                  <span>
                    {columnInfo.name} <PlaceholderText value={columnInfo.suffix} />
                  </span>
                )}
              </Option>
            ))}
          </Menu>
        }
        displayName={displayName}
      />
    </span>
  );
}
